import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Menu, Home, RotateCcw, Package, ShoppingCart,
    ArrowLeftRight, Pill, Truck, LogOut, User, ChevronDown,
    Clipboard,
    IndianRupee, BarChart3, TrendingUp
} from 'lucide-react';
import { FaFileInvoiceDollar } from 'react-icons/fa';
import { MdLocalPharmacy } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../actions/authActions';
import { jwtDecode } from 'jwt-decode';

const Authenticated = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

    const token = useSelector((state) => state.auth.token);
    let username = 'User';
    if (token) {
        try {
            const decoded = jwtDecode(token);
            username = decoded?.name || 'User';
        } catch (_) { }
    }

    const menuItems = [
        { title: 'Dashboard', icon: Home, path: '/dashboard' },
        { title: 'Sales', icon: FaFileInvoiceDollar, path: '/invoice' },
        { title: 'Sales Returns', icon: ArrowLeftRight, path: '/sales-return-list' },
        { title: 'Opening Stock', icon: Clipboard, path: '/opening-stock' },
        { title: 'Stock', icon: Package, path: '/stock' },
        { title: 'Purchase', icon: ShoppingCart, path: '/purchase' },
        { title: 'Purchase Returns', icon: RotateCcw, path: '/purchase-return' },
        { title: 'Medicine', icon: Pill, path: '/medicine' },
        { title: 'Supplier', icon: Truck, path: '/supplier' },
        { title: 'Debit Invoice', icon: IndianRupee, path: '/debit-invoice' },
        { title: 'Monthly Report', icon: BarChart3, path: '/monthly-report' },
        { title: 'Medicine Profit', icon: TrendingUp, path: '/medicine-profit-report' },
        { title: 'Kit Master', icon: Package, path: '/kit' },

    ];

    const handleNavigation = (path) => {
        navigate(path);
        setIsUserDropdownOpen(false);
    };

    const handleLogout = () => {
        dispatch(logout());
        setIsUserDropdownOpen(false);
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* TOP BAR */}
            <header className="fixed top-0 left-0 right-0 h-14 bg-teal-700 text-white flex items-center justify-between px-4 shadow-md z-50">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className="p-1.5 hover:bg-teal-700 rounded-lg transition-colors"
                    >
                        <Menu size={20} />
                    </button>
                    <div className="flex items-center gap-2">
                        <div className="bg-teal-500 p-1.5 rounded-lg">
                            <MdLocalPharmacy size={20} />
                        </div>
                        <span className="text-lg font-bold tracking-wide">Medi X</span>
                    </div>
                </div>

                {/* User Dropdown */}
                <div className="relative">
                    <button
                        onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors text-sm font-medium"
                    >
                        <User size={18} />
                        <span className="hidden sm:inline">{username}</span>
                        <ChevronDown size={16} className={`transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isUserDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                            {/* <button
                                onClick={() => handleNavigation('#')}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 transition-colors"
                            >
                                <UserCircle size={16} />
                                Profile
                            </button>
                            <button
                                onClick={() => handleNavigation('#')}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 transition-colors"
                            >
                                <Settings size={16} />
                                Settings
                            </button> */}
                            <hr className="border-gray-200" />
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                                <LogOut size={16} />
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            </header>

            {/* SIDEBAR */}
            <aside
                className={`fixed left-0 top-14 h-full bg-white shadow-lg border-r border-gray-200 transition-all duration-300 z-40 ${isSidebarOpen ? 'w-64' : 'w-16'
                    }`}
            >
                <nav className="mt-4 px-2">
                    {menuItems.map((item, idx) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;

                        return (
                            <button
                                key={idx}
                                onClick={() => handleNavigation(item.path)}
                                className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all mb-1 
        ${isActive
                                        ? 'bg-teal-700 text-white shadow-sm'
                                        : 'text-gray-600 hover:bg-teal-700 hover:text-white'
                                    } 
        ${!isSidebarOpen && 'justify-center'}`}
                                title={!isSidebarOpen ? item.title : ''}
                            >
                                <Icon
                                    size={20}
                                    className={
                                        isActive
                                            ? 'text-white'
                                            : 'text-gray-500 group-hover:text-white'
                                    }
                                />
                                {isSidebarOpen && <span>{item.title}</span>}
                            </button>
                        );
                    })}
                </nav>
            </aside>

            {/* MAIN CONTENT */}
            <main
                className={`pt-16 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-16'} p-4 md:p-6`}
            >
                <div className="w-full mt-10">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Authenticated;